import { useMutation } from '@apollo/client';
import {
  Body,
  Button,
  FormRadioTileGroup,
  FormTextarea,
  Modal,
  palettes,
  RadioTileGroup,
  useFlashMessage
} from '@indigo-ag/component-module';
import { BackgroundCheckStepStatus, DecisionBackgroundCheckStepStatus } from '@indigo-ag/schema';
import { Form, Formik } from 'formik';
import i18next from 'i18next';
import React, { useContext } from 'react';
import styled from 'styled-components';

import { faBan, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import {
  getOpportunityContactRolesAndBackgroundCheckGroup,
  setBackgroundCheckStepDecision,
  getBackgroundCheckStepEvents
} from 'features/brazil-grower/queries/backgroundCheck.queries';

const StyledButtonGroup = styled.div`
  display: flex;
  justify-content: right;
  margin-bottom: 16px;
`;

const StyledBody = styled.p`
  margin-bottom: 16px;
`;

const StyledModal = styled(Modal)`
  min-width: 50%;
`;

const isOverriding = (stepStatus: BackgroundCheckStepStatus, decision?: DecisionBackgroundCheckStepStatus) => {
  if (decision === undefined) return false;

  return (
    stepStatus === BackgroundCheckStepStatus.SYSTEM_ERROR ||
    (decision === DecisionBackgroundCheckStepStatus.FAILED.valueOf() &&
      [BackgroundCheckStepStatus.PASSED_TENTATIVE.valueOf(), BackgroundCheckStepStatus.PASSED.valueOf()].includes(
        stepStatus
      )) ||
    (decision === DecisionBackgroundCheckStepStatus.PASSED.valueOf() &&
      [BackgroundCheckStepStatus.FAILED_TENTATIVE.valueOf(), BackgroundCheckStepStatus.FAILED.valueOf()].includes(
        stepStatus
      ))
  );
};

interface FormShape {
  decision?: DecisionBackgroundCheckStepStatus;
  comment?: string;
}

interface Props {
  stepId: string;
  stepStatus: BackgroundCheckStepStatus;
  isOpen: boolean;
  onRequestClose: () => void;
}

export const EditBackgroundCheckStepModal = ({ stepId, stepStatus, isOpen, onRequestClose }: Props) => {
  const { orderInfo } = useContext(FinanceProcessContext);
  const opportunityId = orderInfo?.opportunities?.[0].id;

  const [setDecision] = useMutation(setBackgroundCheckStepDecision, {
    refetchQueries: [
      { query: getOpportunityContactRolesAndBackgroundCheckGroup, variables: { opportunityId } },
      { query: getBackgroundCheckStepEvents, variables: { stepId, limit: 5, offset: 0 } }
    ]
  });

  const { addFlashMessage } = useFlashMessage();

  const onSubmit = async ({ decision, comment }: FormShape) => {
    try {
      await setDecision({ variables: { stepId, decision, comment } });
      addFlashMessage({
        content: i18next.t('backgroundCheckStep_EditModal_success'),
        title: '',
        variant: 'success'
      });
      onRequestClose();
    } catch (e) {
      addFlashMessage({
        content: i18next.t('backgroundCheckStep_EditModal_error'),
        title: '',
        variant: 'error'
      });
    }
  };

  const content = (
    <>
      <StyledBody>{i18next.t('backgroundCheckStep_EditModal_body')}</StyledBody>
      <Formik initialValues={{}} onSubmit={onSubmit}>
        {({ isSubmitting, values: { decision } }) => {
          return (
            <Form>
              <FormRadioTileGroup name="decision">
                <RadioTileGroup.Tile
                  icon={faCheck}
                  iconColor={palettes.brand.green.base}
                  value={DecisionBackgroundCheckStepStatus.PASSED}
                  disabled={stepStatus === BackgroundCheckStepStatus.PASSED}
                >
                  <Body>{i18next.t('backgroundCheckStepStatus_Passed')}</Body>
                </RadioTileGroup.Tile>
                <RadioTileGroup.Tile
                  icon={faBan}
                  iconColor={palettes.brand.red.base}
                  value={DecisionBackgroundCheckStepStatus.FAILED}
                  disabled={stepStatus === BackgroundCheckStepStatus.FAILED}
                >
                  <Body>{i18next.t('backgroundCheckStepStatus_Failed')}</Body>
                </RadioTileGroup.Tile>
              </FormRadioTileGroup>
              <FormTextarea
                name="comment"
                required={isOverriding(stepStatus, decision)}
                label={i18next.t('notes')}
                helpText={i18next.t('backgroundCheckStep_EditModal_commentHelpText')}
              />
              <StyledButtonGroup>
                <Button onClick={onRequestClose} variant="secondary">
                  {i18next.t('cancel')}
                </Button>
                <Button busy={isSubmitting} type="submit">
                  {i18next.t('save')}
                </Button>
              </StyledButtonGroup>
            </Form>
          );
        }}
      </Formik>
    </>
  );

  return (
    <StyledModal
      onRequestClose={onRequestClose}
      open={isOpen}
      renderContent={() => content}
      title={i18next.t('backgroundCheckStep_EditModal_title')}
    />
  );
};
